'use client'

import { useEffect, useState } from 'react'
import { createTrackingPoller } from '@/lib/orderTracking/poller'
import type { TrackingSnapshot } from '@/lib/orderTracking/types'

/**
 * Snapshot del seguimiento en vivo para la confirmación: la MISMA ruta y el
 * mismo poller que /p/<código> (`GET /api/pedido/[code]`, cada 10 s, pausa con
 * la pestaña oculta y consulta al volver, se detiene en estado terminal).
 * Sin código de recibo devuelve null y no hace ninguna petición.
 */
export function useOrderProgress(receiptCode: string | null): TrackingSnapshot | null {
  const [snapshot, setSnapshot] = useState<TrackingSnapshot | null>(null)

  useEffect(() => {
    if (!receiptCode) return
    let stopped = false
    let poller: { start(): void; stop(): void } | null = null

    const fetchSnapshot = async (): Promise<TrackingSnapshot | null> => {
      const res = await fetch(`/api/pedido/${encodeURIComponent(receiptCode)}`, { cache: 'no-store' })
      return res.ok ? ((await res.json()) as TrackingSnapshot) : null
    }

    const startPoller = (initial: TrackingSnapshot) => {
      poller = createTrackingPoller(initial, {
        fetchSnapshot,
        onSnapshot: setSnapshot,
        isHidden: () => document.visibilityState === 'hidden',
        onVisible: (cb) => {
          const handler = () => { if (document.visibilityState === 'visible') cb() }
          document.addEventListener('visibilitychange', handler)
          return () => document.removeEventListener('visibilitychange', handler)
        },
      })
      poller.start()
    }

    // Primera lectura de inmediato; el poller sigue desde ahí. Si falla, el
    // poller arranca igual (como activo) y reintenta en el siguiente tick.
    fetchSnapshot()
      .catch(() => null)
      .then((first) => {
        if (stopped) return
        if (first) setSnapshot(first)
        startPoller(first ?? ({ active: true } as TrackingSnapshot))
      })

    return () => {
      stopped = true
      poller?.stop()
    }
  }, [receiptCode])

  return snapshot
}
